/**
 * @module off-canvas-panel
 */

import {
    panelUpdater,
    setPanelLoading,
    setPanelLoaded,
    type PanelUpdaterArgs,
    type PanelLoadingArgs
} from '@remarkablenet/rcm-utils/panel-updater';
import { fetchText } from '../fetch-helper/fetch-helper';
import { activate, deactivate, visuallyDeactivate, toggle } from '../css-transition-helper/css-transition-helper';
import {
  getAbsoluteHeight,
  convertObjectValuesToArray,
  checkForModuleToLoad,
  getModuleToLoad,
  bindModuleToLoad,
  getSrcToLoad,
  disableScroll,
  enableScroll
} from '../utility-functions/utility-functions';
import { addClass, removeClass } from '@remarkablenet/rcm-utils/class-helpers';
import { appendScript, removeAppendedScript } from '@remarkablenet/rcm-utils/script-helpers';
import { addDelegateEventListener, DelegateEvent } from '@remarkablenet/rcm-utils/add-delegate-event-listener';
import { dispatchEventWrapper } from '../event/event';

const PANEL_JS_LISTENER_CLASS_NAME = 'js-open-off-canvas-panel';
const PANEL_CLASS_NAME = 'off-canvas-panel';
const PANEL_BACKDROP_NAME = 'off-canvas-panel-backdrop';
const PANEL_MODIFIER_PREFIX = `${PANEL_CLASS_NAME}--`;
const PANEL_ACTIVE_STATE = 'is-active';
const PANEL_VISIBLE_STATE = 'is-visible';

const PanelPositions = Object.freeze({
  TOP: 'top',
  RIGHT: 'right',
  BOTTOM: 'bottom',
  LEFT: 'left'
});

const ocpOptions = Object.freeze({
  position: PanelPositions.BOTTOM,
  colour: 'white',
  autoHeight: true,
  extraClasses: '',
  hideCloseButton: false
});

const getCustomEvents = (el) => {
  let eventsArray = new Array();
  let events = el.dataset.customEvents;
  if (events) eventsArray['names'] = events.split(',');
  eventsArray['data'] = el.dataset.customEventData ? JSON.parse(el.dataset.customEventData) : null;
  return eventsArray;
};

const triggerCustomEvents = (events) => {
  if (events.names) {
    events.names.forEach((event) => {
      if (event !== undefined) {
        dispatchEventWrapper(document, event, events.data);
      }
    });
  }
};

export const addOffCanvasPanelListeners = (context = document) => {
    const panelLinks = context.querySelectorAll(`.${PANEL_JS_LISTENER_CLASS_NAME}`);

  panelLinks.forEach(async (panelLink) => {
    panelLink.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();

      if (getAjaxUrl(panelLink)) {
        handleAjaxRequest(panelLink);
        let events = getCustomEvents(panelLink);
        if (events) triggerCustomEvents(events);
        return;
      } else if (getHtmlSelector(panelLink)) {
        await handleHtmlRender(panelLink, () => {
          let events = getCustomEvents(panelLink);
          if (events) triggerCustomEvents(events);
        });
        return;
      }

      console.error('Off Canvas Panel Link Clicked but no URL or HTML Selector Given');
      // Open ID Given
    });
  });
};

export const addOffCanvasPanelDelegateListeners = (context = document) => {
  addDelegateEventListener({
    container: context,
    event: 'click',
    target: `.${PANEL_JS_LISTENER_CLASS_NAME}`,
    callback: async (e: DelegateEvent) => {
      e.originalEvent.preventDefault();
      e.originalEvent.stopImmediatePropagation();

      const panelLink = e.delegateTarget as HTMLElement;

      panelLink.style.pointerEvents = 'none';
      setTimeout(() => { panelLink.style.pointerEvents = 'auto'; }, 300);

      if (getAjaxUrl(panelLink)) {
        handleAjaxRequest(panelLink);
        let events = getCustomEvents(panelLink);
        if (events) triggerCustomEvents(events);
        return;
      } else if (getHtmlSelector(panelLink)) {
        await handleHtmlRender(panelLink, () => {
          let events = getCustomEvents(panelLink);
          if (events) triggerCustomEvents(events);
        });
        return;
      }

      console.error('Off Canvas Panel Link Clicked but no URL or HTML Selector Given');
    },
  });
};



export const getAjaxUrl = (elm: Element) => {
  if (elm) return elm.getAttribute('data-href') || elm.getAttribute('href');
};
export const getHtmlSelector = (elm: Element) => {
  if (elm) return elm.getAttribute('data-ocp-selector');
};

export const addCloseCurrentButtonEventListeners = (panel, shouldDestroyOnClose = true) => {
  const buttons = panel.querySelectorAll('.js-close-current-off-canvas-panel');
  if (buttons)
    buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        let events = getCustomEvents(panel);
        if (events) triggerCustomEvents(events);
        closeOffCanvasPanel(panel, shouldDestroyOnClose);
        fireCloseEvent(panel);
      });
    });
};

export const addCloseButtonEventListeners = (panel, shouldDestroyOnClose = true) => {
  const buttons = panel.querySelectorAll('.js-close-off-canvas-panel');
  if (buttons)
    buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        let events = getCustomEvents(panel);
        if (events) triggerCustomEvents(events);
        // Moved into closeOffCanvasPanel to allow backdrop to be removed on last panel closed
        //handleCloseEvent(shouldDestroyOnClose);
        closeOffCanvasPanel(panel, shouldDestroyOnClose);
        fireCloseEvent(panel);
      });
    });
};

const fireCloseEvent = (panel) => {
  const event = new CustomEvent('panel.deactivated', { detail: { element: panel } });
  panel.dispatchEvent(event);
};

export const watchForPanelOpening = (panel) => panel.addEventListener('element.visible.activation', setPanelHeight);

export const setPanelHeight = (event) => {
  const panel = event.currentTarget;
  const child = panel.firstElementChild;
  const height = getAbsoluteHeight(child);
  panel.style.height = `${height}px`;
};

export const buildOptionsFromDataAttributes = (elm) => mapNodeMapIntoObjectOfOptions(elm);

export const mapNodeMapIntoObjectOfOptions = (nodeMap) => {
  let object = {};

  for (let i = 0; i < nodeMap.attributes.length; i++) {
    const attr = nodeMap.attributes[i];

    if (/^data-ocp/.test(attr.nodeName)) {
      let key = attr.nodeName.replace(/^data-ocp-/, '');
      let keyArray = key.split('-');
      let keyArrayFormatted = keyArray.map(uppercaseFirstCharacterInString);

      keyArrayFormatted[0] = keyArrayFormatted[0].toLowerCase();

      let finalKey = keyArrayFormatted.join('');

      // set value variable to nodeValue if it is not a boolean`
      // otherwise set it to true or false
      let value = attr.nodeValue;
      if (value === 'true') {
        value = true;
      } else if (value === 'false') {
        value = false;
      }

      object[finalKey] = value;
    }
  }

  return object;
};

export const visuallyDeactivateListOfPanels = (panels) => {
  panels.forEach((panels) => {
    visuallyDeactivate(panels);
  });
};

export const openOffCanvasPanel = (currentPanel, showBackdrop = false, shouldDestroyOnClose = false) => {
  const allActivePanels = selectAllActiveAndVisibleOffCanvasPanels();

  addCloseCurrentButtonEventListeners(currentPanel, shouldDestroyOnClose);
  addCloseButtonEventListeners(currentPanel, shouldDestroyOnClose);

  if (!checkIfNodeListIsEmpty(allActivePanels)) {
    visuallyDeactivateListOfPanels(allActivePanels);
    activate(currentPanel);
    return;
  }

  if (showBackdrop) {
    createBackdrop().then((backdrop) => {
      activate(backdrop);
      backdrop.addEventListener('click', () => {
        handleCloseEvent(shouldDestroyOnClose);
        backdropHandleClose();
      });
    });
  }

  // Lock scroll when the first off-canvas panel is opened
  disableScroll();

  // Skip when opening nav: adding the class (position: relative, margin-right) conflicts
  // with html.disable-scroll and causes the page to shift on open and on close (scrollbar/layout).
  if (!document.body.classList.contains('menu-open')) document.querySelector('html').classList.add('off-canvas-panel-active');

  activate(currentPanel);
};

export const handleCloseEvent = (shouldDestroyOnClose) => {
  destroyAllTemporaryVisuallyHiddenPanels();
  closeListOfOffCanvasPanels(selectAllVisibleOffCanvasPanels(), shouldDestroyOnClose);
};

export const destroyAllTemporaryVisuallyHiddenPanels = () => {
  const panels = selectAllTemporaryVisuallyHiddenOffCanvasPanels();
  if (!checkIfNodeListIsEmpty(panels)) {
    destroyListOfPanels(panels);
  }
};

export const closeListOfOffCanvasPanels = (panels, shouldDestroy = false) => {
  panels.forEach((panel) => {
    closeOffCanvasPanel(panel, shouldDestroy);
  });
};

export const closeOffCanvasPanel = (panel, shouldDestroy = false) => {

  deactivate(panel).then(() => {
    if (shouldDestroy) {
      removeAppendedScript();
      destroyPanel(panel);
    }
    backdropHandleClose();
  });

};

/**
 * Handles the closing of the backdrop when all panels are deactivated.
 *
 * @remarks This function has to be a timeout in the current implementation to allow the
 * animation to finish before checking to removing the backdrop entirely due to other panels on the page.
 *
 * @returns {void}
 */
export const backdropHandleClose = (): void => {
  setTimeout(() => {
    const remainingPanels = selectAllActiveAndVisibleOffCanvasPanels();
    if (checkIfNodeListIsEmpty(remainingPanels)) {
      const activeBackdrop = isABackdropActive();
      if (activeBackdrop) {
        deactivate(activeBackdrop).then(destroyBackdrop);
      }
      document.querySelector('html').classList.remove('off-canvas-panel-active');
      // Re-enable scroll once all off-canvas panels and backdrop are closed
      enableScroll();
    }
  }, 10);
}

const _setPosition = (panelPositions, elm, position) => {
  const positionsArray = convertObjectValuesToArray(panelPositions);

  resetOffCanvasPanelPosition(elm, positionsArray);
  addClass({ elm, className: [`${PANEL_MODIFIER_PREFIX}${position}`] });

};

export const createOffCanvasPanelInstance = (options = {}) => {
  return new Promise((resolve) => {
    const settings = { ...ocpOptions, ...options };

    const positionClass = `${PANEL_MODIFIER_PREFIX}${settings.position}`;
    const colourClass = `${PANEL_MODIFIER_PREFIX}${settings.colour}`;
    const autoHeightClass = settings.autoHeight === true ? `${PANEL_MODIFIER_PREFIX}auto-height` : '';
    const extraClass = settings.extraClasses !== '' ? settings.extraClasses : '';
    const hideCloseButton = settings.hideCloseButton ? `${PANEL_MODIFIER_PREFIX}hide-close-btn` : '';

    const temporaryClass = `${PANEL_MODIFIER_PREFIX}temporary`;

    const panelHtml = document.createElement('div');
    const panelCloseBtn = document.createElement('div');

    panelHtml.setAttribute(
      'class',
      `${PANEL_CLASS_NAME} ${extraClass} ${positionClass} ${colourClass} ${autoHeightClass} ${hideCloseButton} ${temporaryClass}`
    );
    panelCloseBtn.setAttribute('class', `${PANEL_CLASS_NAME}__close js-close-${PANEL_CLASS_NAME}`);
    if (settings['customEvents']) panelHtml.dataset.customEvents = settings['customEvents'];

    document.body.appendChild(panelHtml);

    resolve({
      panel: panelHtml,
      closeBtn: panelCloseBtn
    });
  }) as Promise<object>;
};

export const createBackdrop = () => {
  return new Promise((resolve) => {
    if (isABackdropActive()) return resolve(selectBackdrop());
    const backdropHtml = document.createElement('div');
    backdropHtml.setAttribute('class', PANEL_BACKDROP_NAME);

    document.body.appendChild(backdropHtml);
    resolve(backdropHtml);
  }) as Promise<HTMLElement>;
};

export const destroyListOfPanels = (panels) => {
  panels.forEach((panel) => {
    destroyPanel(panel);
  });
};

export const selectAllOffCanvasPanels = (context = document) => context.querySelectorAll(`.${PANEL_CLASS_NAME}`);
export const selectAllTemporaryOffCanvasPanels = (context = document) =>
  context.querySelectorAll(`.${PANEL_MODIFIER_PREFIX}temporary`);
export const selectAllTemporaryVisuallyHiddenOffCanvasPanels = (context = document) =>
  context.querySelectorAll(`.${PANEL_MODIFIER_PREFIX}temporary.${PANEL_VISIBLE_STATE}:not(.${PANEL_ACTIVE_STATE})`);
export const selectAllActiveOffCanvasPanels = (context = document) =>
  context.querySelectorAll(`.${PANEL_CLASS_NAME}.${PANEL_ACTIVE_STATE}`);
export const selectAllVisibleOffCanvasPanels = (context = document) =>
  context.querySelectorAll(`.${PANEL_CLASS_NAME}.${PANEL_VISIBLE_STATE}`);
export const selectAllActiveAndVisibleOffCanvasPanels = (context = document) =>
  context.querySelectorAll(`.${PANEL_CLASS_NAME}.${PANEL_ACTIVE_STATE}.${PANEL_VISIBLE_STATE}`);

export const visuallyDeactivatePanel = (panel) => panel.classList.remove(PANEL_ACTIVE_STATE);
export const visuallyActivatePanel = (panel) => panel.classList.add(PANEL_ACTIVE_STATE);

export const setOffCanvasPanelPosition = _setPosition.bind(this, PanelPositions);
export const resetOffCanvasPanelPosition = (elm, panelPositions) =>
  panelPositions.forEach((position) =>
    removeClass({ elm, className: [`${PANEL_MODIFIER_PREFIX}${position}`] })
  );

export const findClosestOffCanvasPanel = (elm) => elm.closest(`.${PANEL_CLASS_NAME}`);
export const findClosestOffCanvasPanelLink = (elm) =>
  elm.classList.contains(PANEL_JS_LISTENER_CLASS_NAME) ? elm : elm.closest(`.${PANEL_JS_LISTENER_CLASS_NAME}`);

export const destroyPanel = (panel) => panel.remove();
export const destroyBackdrop = () =>
  document.querySelectorAll(`.${PANEL_BACKDROP_NAME}`).forEach((item) => item.remove());

export const checkIfNodeListIsEmpty = (nodeListOfPanels) => nodeListOfPanels.length === 0;
export const isABackdropActive = () =>
  document.querySelector(`.${PANEL_BACKDROP_NAME}.${PANEL_ACTIVE_STATE}.${PANEL_VISIBLE_STATE}`) as HTMLElement;
export const selectBackdrop = (context = document) => context.querySelector(`.${PANEL_BACKDROP_NAME}`) as HTMLElement;
const uppercaseFirstCharacterInString = (string) => string.charAt(0).toUpperCase() + string.slice(1);

export const handleAjaxRequest = (panelLink, callback?: Function) => {
  if (panelLink) {
    let url = panelLink.getAttribute('data-href') || panelLink.getAttribute('href');
    url.indexOf('?') >= 0 ? (url += '&') : (url += '?');
    url += 'nolayout=true';

    const options = buildOptionsFromDataAttributes(panelLink);

    createOffCanvasPanelInstance(options).then(async (panelElements) => {
      const panel = panelElements['panel'];
      const html = await panelUpdater(panel, fetchText(url));

      panel.innerHTML = html;
      panel.appendChild(panelElements['closeBtn']);

      if (checkForModuleToLoad(panel)) {
        const module = getModuleToLoad(panel);
        const moduleSrc = getSrcToLoad(checkForModuleToLoad(panel));

        appendScript(moduleSrc).then(() => {
          bindModuleToLoad(module);
        });
      }

      watchForPanelOpening(panel);
      openOffCanvasPanel(panel, true, true);
      if (callback) callback();
    });
  }
};

export const handleHtmlRender = (panelLink, callback?: Function) => {
  if (!panelLink) return;
  return new Promise<void>((resolve) => {
    const options = buildOptionsFromDataAttributes(panelLink);

    createOffCanvasPanelInstance(options).then(async (panelElements) => {
      const panel = panelElements['panel'];
      const html = getHtmlToLoad(panelLink);

      panel.innerHTML = html;
      panel.appendChild(panelElements['closeBtn']);

      if (checkForModuleToLoad(panel)) {
        const module = getModuleToLoad(panel);
        const moduleSrc = getSrcToLoad(checkForModuleToLoad(panel));

        appendScript(moduleSrc).then(() => {
          bindModuleToLoad(module);
        });
      }

      watchForPanelOpening(panel);
      openOffCanvasPanel(panel, true, true);
      if (callback) callback();
      resolve();
    });
  });
};

export const loadExistingHtmlIntoPanel = (existingHtml: HTMLElement, callback?: Function) => {
  if (!existingHtml) return;
  return new Promise<void>((resolve) => {
    const options = buildOptionsFromDataAttributes(existingHtml);

    createOffCanvasPanelInstance(options).then(async (panelElements) => {
      const panel = panelElements['panel'];
      const html = existingHtml.innerHTML;

      panel.innerHTML = html;
      panel.appendChild(panelElements['closeBtn']);

      if (checkForModuleToLoad(panel)) {
        const module = getModuleToLoad(panel);
        const moduleSrc = getSrcToLoad(checkForModuleToLoad(panel));

        appendScript(moduleSrc).then(() => {
          bindModuleToLoad(module);
        });
      }

      watchForPanelOpening(panel);
      openOffCanvasPanel(panel, true, true);
      if (callback) callback();
      resolve();
    });
  });
};

export const getHtmlToLoad = (panelLink) => {
  const selector = getHtmlSelector(panelLink);
  if (!selector) return;
  return document.querySelector(selector)?.innerHTML;
};
